import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '@/stores/auth';
import { isIosDevice, isStandaloneApp, shouldShowInstallPrompt } from '@/lib/pwaInstall';
import {
  canUsePush,
  dismissPushPrompt,
  isPushPromptDismissed,
  pushPermissionState,
  requestPushPermissionAndRegister,
} from '@/lib/pushNotifications';
import { toast } from '@/components/ui/Toast';

/**
 * Asks for notification permission via a real button tap (browsers block
 * automatic prompts). Shown until granted, denied, dismissed, or unsupported.
 */
export function EnablePushBanner() {
  const firebaseUser = useAuth((s) => s.firebaseUser);
  const pushOn = useAuth((s) => s.userDoc?.preferences?.notificationChannels?.push ?? true);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [iosNeedsInstall, setIosNeedsInstall] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!firebaseUser || !pushOn || isPushPromptDismissed()) {
        if (!cancelled) setVisible(false);
        return;
      }
      if (!(await canUsePush())) {
        if (!cancelled) setVisible(false);
        return;
      }
      const permission = pushPermissionState();
      if (permission !== 'default') {
        if (!cancelled) setVisible(false);
        return;
      }
      // Don't stack on top of the install banner. While install is being offered
      // (Android installable, or iOS-not-standalone) we defer — the install
      // banner already explains that installing enables push. Once the app is
      // installed or the install prompt is dismissed, this banner reappears.
      if (shouldShowInstallPrompt()) {
        if (!cancelled) setVisible(false);
        return;
      }
      // iOS only allows Web Push from an installed Home Screen app.
      if (isIosDevice() && !isStandaloneApp()) {
        if (!cancelled) {
          setIosNeedsInstall(true);
          setVisible(true);
        }
        return;
      }
      if (!cancelled) {
        setIosNeedsInstall(false);
        setVisible(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, pushOn]);

  if (!visible) return null;

  const dismiss = () => {
    dismissPushPrompt();
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const result = await requestPushPermissionAndRegister();
      if (result === 'granted') {
        toast.success('Push notifications on.');
        setVisible(false);
      } else if (result === 'denied') {
        toast.error('Permission denied — you can enable it later in Settings.');
        setVisible(false);
      } else {
        toast.error('Push isn’t available in this browser.');
        setVisible(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-makhe-line bg-makhe-paper">
      <div className="mx-auto flex max-w-6xl items-start gap-3 px-5 py-3">
        <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-makhe-primary/10 text-makhe-primary">
          <Bell size={18} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-display text-sm">Stay on top of bookings</p>
          {iosNeedsInstall ? (
            <p className="mt-1 text-xs leading-relaxed text-makhe-muted">
              On iPhone, install Makhe to your Home Screen first — then open it from there to turn
              on push alerts.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-makhe-muted">
                Get a ping when someone books you, accepts, or needs confirmation.
              </p>
              <button
                type="button"
                onClick={() => void enable()}
                disabled={busy}
                className="mt-2 rounded-full bg-makhe-ink px-4 py-1.5 text-[10px] uppercase tracking-widest text-makhe-bg disabled:opacity-60"
              >
                {busy ? 'Enabling…' : 'Turn on notifications'}
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss notification prompt"
          className="shrink-0 rounded-full p-1 text-makhe-muted hover:text-makhe-ink"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
